import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleContext";
import { isDomesticLocale } from "../../i18n/languages";
import {
  OPEN_SMART_SELECTOR_EVENT,
  consumePendingSmartSelectorOpen,
} from "../../constants/openSmartSelector";
import { SELECTOR_COUNTRIES } from "../../data/selectorCountries";
import { submitLead } from "../../services/submitLead";
import {
  fetchClientIp,
  isAutoPromptSuppressed,
  markAutoPromptDismissed,
} from "../../utils/autoPromptGate";
import { lockBodyScroll, unlockBodyScroll } from "../../utils/bodyScrollLock";
import "./index.css";

const INITIAL_FORM = {
  country: "",
  phone: "",
  wechat: "",
  whatsapp: "",
  email: "",
  product: "",
  size: "",
  output: "",
  budget: "",
};

const FAB_ENGAGE_DELAY_MS = 20000;
const AUTO_OPEN_DELAY_MS = 60000;
const FAB_TEASER_DISMISSED_KEY = "yongyuan-selector-teaser-dismissed";

function SmartSelector() {
  const { t, language } = useLocale();
  const isDomestic = isDomesticLocale(language);
  const [open, setOpen] = useState(false);
  const [centered, setCentered] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [fabEngaged, setFabEngaged] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(
    () => sessionStorage.getItem(FAB_TEASER_DISMISSED_KEY) === "true"
  );
  const openRef = useRef(open);
  const clientIpRef = useRef("");
  const centeredRef = useRef(centered);
  const autoPromptTimerRef = useRef(null);
  const autoPromptCancelledRef = useRef(false);
  openRef.current = open;
  centeredRef.current = centered;

  const dismissTeaser = () => {
    setTeaserDismissed(true);
    sessionStorage.setItem(FAB_TEASER_DISMISSED_KEY, "true");
  };

  const rememberAutoPromptDismissal = () => {
    markAutoPromptDismissed(clientIpRef.current);
  };

  /** 取消本页尚未触发的自动弹窗；不写入 24h 抑制 */
  const cancelPendingAutoPrompt = () => {
    autoPromptCancelledRef.current = true;
    if (autoPromptTimerRef.current != null) {
      window.clearTimeout(autoPromptTimerRef.current);
      autoPromptTimerRef.current = null;
    }
  };

  const openSelectorManually = () => {
    cancelPendingAutoPrompt();
    setCentered(false);
    setOpen(true);
    setSubmitted(false);
    setSubmitError("");
    dismissTeaser();
  };

  const resetPanelState = () => {
    setOpen(false);
    setCentered(false);
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError("");
    setForm(INITIAL_FORM);
  };

  useEffect(() => {
    const handleOpenSelector = () => {
      consumePendingSmartSelectorOpen();
      openSelectorManually();
    };

    window.addEventListener(OPEN_SMART_SELECTOR_EVENT, handleOpenSelector);

    if (consumePendingSmartSelectorOpen()) {
      openSelectorManually();
    }

    return () => {
      window.removeEventListener(OPEN_SMART_SELECTOR_EVENT, handleOpenSelector);
    };
  }, []);

  useEffect(() => {
    if (open) return undefined;

    const timer = window.setTimeout(() => {
      setFabEngaged(true);
    }, FAB_ENGAGE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [open, language]);

  useEffect(() => {
    let cancelled = false;

    const setupAutoPrompt = async () => {
      const ip = await fetchClientIp();
      if (cancelled || autoPromptCancelledRef.current) return;

      clientIpRef.current = ip;

      if (isAutoPromptSuppressed(ip)) {
        return;
      }

      autoPromptTimerRef.current = window.setTimeout(() => {
        autoPromptTimerRef.current = null;
        if (cancelled || autoPromptCancelledRef.current) return;
        // 用户已手动打开：本页不再自动弹，且不记成「已提示」
        if (openRef.current) return;

        dismissTeaser();
        setSubmitted(false);
        setSubmitError("");
        setCentered(true);
        setOpen(true);
        // 真正弹出后立即写入抑制，避免未关窗刷新再次自动弹
        rememberAutoPromptDismissal();
      }, AUTO_OPEN_DELAY_MS);
    };

    setupAutoPrompt();

    return () => {
      cancelled = true;
      if (autoPromptTimerRef.current != null) {
        window.clearTimeout(autoPromptTimerRef.current);
        autoPromptTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (centeredRef.current) {
        rememberAutoPromptDismissal();
      }
      resetPanelState();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      country: "",
      phone: "",
      wechat: "",
      whatsapp: "",
      email: "",
    }));
  }, [language]);

  const handleOpen = () => {
    openSelectorManually();
  };

  const handleDismissTeaser = (event) => {
    event.stopPropagation();
    dismissTeaser();
  };

  const showFabTeaser = fabEngaged && !open && !teaserDismissed;

  const handleClose = () => {
    if (centered) {
      rememberAutoPromptDismissal();
    }
    resetPanelState();
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const outputOptions = [
    { value: "1-50", label: t("selectorOutput1") },
    { value: "50-200", label: t("selectorOutput2") },
    { value: "200-500", label: t("selectorOutput3") },
    { value: "500-1000", label: t("selectorOutput4") },
    { value: "1000+", label: t("selectorOutput5") },
  ];

  const budgetOptions = isDomestic
    ? [
        { value: "under-20k", label: t("selectorBudget1") },
        { value: "20k-50k", label: t("selectorBudget2") },
        { value: "50k-100k", label: t("selectorBudget3") },
        { value: "over-100k", label: t("selectorBudget4") },
        { value: "undecided", label: t("selectorBudget5") },
      ]
    : [
        { value: "under-3000", label: t("selectorBudget1") },
        { value: "3000-6000", label: t("selectorBudget2") },
        { value: "6000-10000", label: t("selectorBudget3") },
        { value: "10000-20000", label: t("selectorBudget4") },
        { value: "over-20000", label: t("selectorBudget5") },
        { value: "undecided", label: t("selectorBudget6") },
      ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitting(true);

    const outputLabel =
      outputOptions.find((item) => item.value === form.output)?.label || form.output;
    const budgetLabel =
      budgetOptions.find((item) => item.value === form.budget)?.label || form.budget;
    const countryEntry = SELECTOR_COUNTRIES.find((item) => item.code === form.country);

    try {
      await submitLead({
        source: "smart-selector",
        region: isDomestic ? "domestic" : "international",
        locale: language,
        contact: isDomestic
          ? { phone: form.phone, wechat: form.wechat }
          : {
              country: countryEntry ? t(countryEntry.labelKey) : form.country,
              whatsapp: form.whatsapp,
              email: form.email,
            },
        requirements: {
          product: form.product,
          size: form.size,
          outputLabel,
          budgetLabel,
        },
      });
      // 提交成功即抑制自动弹窗，避免未关窗刷新后再次弹出
      rememberAutoPromptDismissal();
      cancelPendingAutoPrompt();
      setSubmitted(true);
    } catch {
      setSubmitError(t("selectorSubmitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const successItems = [
    t("selectorSuccessItem1"),
    t("selectorSuccessItem2"),
    t("selectorSuccessItem3"),
    t("selectorSuccessItem4"),
    t("selectorSuccessItem5"),
  ];

  return (
    <>
      <div
        className={`smart-selector-fab-wrap${
          fabEngaged ? " smart-selector-fab-wrap--engaged" : ""
        }`}
      >
        {showFabTeaser && (
          <div
            className={`smart-selector-fab-teaser${
              isDomestic ? "" : " smart-selector-fab-teaser--intl"
            }`}
            role="complementary"
            aria-live="polite"
          >
            <button
              type="button"
              className="smart-selector-fab-teaser-close"
              onClick={handleDismissTeaser}
              aria-label={t("selectorClose")}
            >
              ×
            </button>

            {isDomestic ? (
              <button
                type="button"
                className="smart-selector-fab-teaser-body"
                onClick={handleOpen}
              >
                <p className="smart-selector-fab-teaser-title">
                  {t("selectorFabTeaserTitle")}
                </p>
                <p className="smart-selector-fab-teaser-subtitle">
                  {t("selectorFabTeaserSubtitle")}
                </p>
              </button>
            ) : (
              <div className="smart-selector-fab-teaser-content">
                <p className="smart-selector-fab-teaser-title">
                  {t("selectorFabTeaserTitle")}
                </p>
                <p className="smart-selector-fab-teaser-trust">
                  {t("selectorTrustBadge")}
                </p>
                <p className="smart-selector-fab-teaser-subtitle">
                  {t("selectorFabTeaserSubtitle")}
                </p>
                <button
                  type="button"
                  className="smart-selector-fab-teaser-cta"
                  onClick={handleOpen}
                >
                  {t("selectorFabTeaserCta")}
                </button>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className={`smart-selector-fab${
            fabEngaged ? " smart-selector-fab--pulse" : ""
          }`}
          onClick={handleOpen}
          aria-label={t("selectorTrigger")}
        >
          <span className="smart-selector-fab-icon" aria-hidden="true">
            ✦
          </span>
          <span className="smart-selector-fab-text">{t("selectorTrigger")}</span>
        </button>
      </div>

      {open && (
        <div
          className={`smart-selector-overlay${
            centered ? " smart-selector-overlay--centered" : ""
          }`}
          onClick={handleClose}
        >
          <div
            className="smart-selector-panel yy-hide-scrollbar"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smart-selector-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="smart-selector-close"
              onClick={handleClose}
              aria-label={t("selectorClose")}
            >
              ×
            </button>

            {!submitted ? (
              <>
                <h2 id="smart-selector-title" className="smart-selector-title">
                  {t("selectorTitle")}
                </h2>
                <p className="smart-selector-subtitle">{t("selectorSubtitle")}</p>

                <form className="smart-selector-form" onSubmit={handleSubmit}>
                  <div className="smart-selector-section">
                    <h3 className="smart-selector-section-title">
                      {t("selectorContactSection")}
                    </h3>

                    {isDomestic ? (
                      <>
                        <label className="smart-selector-field">
                          <span>
                            {t("selectorPhoneLabel")}
                            <em className="smart-selector-required">
                              {t("selectorRequired")}
                            </em>
                          </span>
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={handleChange("phone")}
                            placeholder={t("selectorPhonePlaceholder")}
                            required
                          />
                        </label>

                        <label className="smart-selector-field">
                          <span>
                            {t("selectorWechatLabel")}
                            <em className="smart-selector-optional">
                              {t("selectorOptional")}
                            </em>
                          </span>
                          <input
                            type="text"
                            value={form.wechat}
                            onChange={handleChange("wechat")}
                            placeholder={t("selectorWechatPlaceholder")}
                          />
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="smart-selector-field">
                          <span>
                            {t("selectorCountryLabel")}
                            <em className="smart-selector-required">
                              {t("selectorRequired")}
                            </em>
                          </span>
                          <select
                            className={
                              form.country ? "" : "smart-selector-select-placeholder"
                            }
                            value={form.country}
                            onChange={handleChange("country")}
                            required
                          >
                            <option value="" disabled hidden>
                              {t("selectorCountryPlaceholder")}
                            </option>
                            {SELECTOR_COUNTRIES.map((item) => (
                              <option key={item.code} value={item.code}>
                                {t(item.labelKey)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="smart-selector-field">
                          <span>
                            {t("selectorWhatsappLabel")}
                            <em className="smart-selector-required">
                              {t("selectorRequired")}
                            </em>
                          </span>
                          <input
                            type="tel"
                            value={form.whatsapp}
                            onChange={handleChange("whatsapp")}
                            placeholder={t("selectorWhatsappPlaceholder")}
                            required
                          />
                        </label>

                        <label className="smart-selector-field">
                          <span>
                            {t("selectorEmailLabel")}
                            <em className="smart-selector-optional">
                              {t("selectorOptional")}
                            </em>
                          </span>
                          <input
                            type="email"
                            value={form.email}
                            onChange={handleChange("email")}
                            placeholder={t("selectorEmailPlaceholder")}
                          />
                        </label>
                      </>
                    )}
                  </div>

                  <label className="smart-selector-field">
                    <span>{t("selectorProductLabel")}</span>
                    <input
                      type="text"
                      value={form.product}
                      onChange={handleChange("product")}
                      placeholder={t("selectorProductPlaceholder")}
                      required
                    />
                    <small>{t("selectorProductHint")}</small>
                  </label>

                  <label className="smart-selector-field">
                    <span>{t("selectorSizeLabel")}</span>
                    <input
                      type="text"
                      value={form.size}
                      onChange={handleChange("size")}
                      placeholder={t("selectorSizePlaceholder")}
                    />
                    <small>{t("selectorSizeHint")}</small>
                  </label>

                  <label className="smart-selector-field">
                    <span>{t("selectorOutputLabel")}</span>
                    <select
                      className={
                        form.output ? "" : "smart-selector-select-placeholder"
                      }
                      value={form.output}
                      onChange={handleChange("output")}
                      required
                    >
                      <option value="" disabled hidden>
                        {t("selectorSelectPlaceholder")}
                      </option>
                      {outputOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="smart-selector-field">
                    <span>{t("selectorBudgetLabel")}</span>
                    <select
                      className={
                        form.budget ? "" : "smart-selector-select-placeholder"
                      }
                      value={form.budget}
                      onChange={handleChange("budget")}
                      required
                    >
                      <option value="" disabled hidden>
                        {t("selectorSelectPlaceholder")}
                      </option>
                      {budgetOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="submit"
                    className="smart-selector-submit"
                    disabled={submitting}
                  >
                    {submitting ? t("selectorSubmitting") : t("selectorSubmit")}
                  </button>
                  {submitError && (
                    <p className="smart-selector-submit-error" role="alert">
                      {submitError}
                    </p>
                  )}
                  {!isDomestic && (
                    <div className="smart-selector-trust">
                      <p className="smart-selector-trust-badge">
                        {t("selectorTrustBadge")}
                      </p>
                      <p className="smart-selector-trust-badge">
                        {t("selectorTrustDelivered")}
                      </p>
                    </div>
                  )}
                </form>
              </>
            ) : (
              <div className="smart-selector-success">
                <div className="smart-selector-success-icon" aria-hidden="true">
                  ✓
                </div>
                <h2 className="smart-selector-title">{t("selectorSuccessTitle")}</h2>
                <p
                  className={`smart-selector-success-desc${
                    isDomestic ? " smart-selector-success-desc--solo" : ""
                  }`}
                >
                  {t("selectorSuccessDesc")}
                </p>
                {!isDomestic && (
                  <p className="smart-selector-success-receive">
                    {t("selectorSuccessReceive")}
                  </p>
                )}
                <ul className="smart-selector-success-list">
                  {successItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="smart-selector-submit"
                  onClick={handleClose}
                >
                  {t("selectorClose")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default SmartSelector;
